import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Skeleton,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { orderApi } from '../services/api';
import {
  ORDER_STATUSES,
  getLogisticsStatus,
  formatUsd,
} from '../utils/orderPresentation';

const OrderDetail = ({ visible, order, onClose, navigation, onAfterClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [requestedId, setRequestedId] = useState(null);
  const contentRef = useRef(null);
  const navigateOrder = (navigate) => {
    // Keep Escape and Tab inside the drawer when a boundary disables the clicked button.
    contentRef.current?.focus({ preventScroll: true });
    navigate();
  };
  useEffect(() => {
    if (!visible || !order) return undefined;
    let current = true;
    setRequestedId(order.id);
    const body = contentRef.current?.closest('.ant-drawer-body');
    if (body) body.scrollTop = 0;
    setDetail(null);
    setLoading(true);
    setError('');
    orderApi
      .getOrderDetail(order.id)
      .then((response) => {
        if (
          !response.data ||
          (response.data.code && response.data.code !== 200) ||
          response.data.id == null ||
          String(response.data.id) !== String(order.id)
        )
          throw new Error(response.data?.message || '暂时无法读取订单详情');
        if (current) setDetail(response.data);
      })
      .catch((failure) => {
        if (current) setError(failure.message || '详情加载失败');
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [visible, order, revision]);
  const isLoading = loading || (visible && order?.id !== requestedId);
  const currentError = order?.id === requestedId ? error : '';
  const loadedOrder =
    detail && String(detail.id) === String(order?.id) ? detail : null;
  const currentOrder = loadedOrder || order || {};
  const logisticsStatus = getLogisticsStatus(currentOrder);
  const hasSummary = Boolean(loadedOrder || order?.orderNo || order?.storeName);
  const status = (value, options, description) => {
    const item = options[value] || { text: '未知状态', tone: 'neutral' };
    return (
      <span className={'order-status ' + item.tone}>
        {description || item.text}
      </span>
    );
  };
  const copyable = (value) =>
    value ? <Typography.Text copyable>{value}</Typography.Text> : '-';
  const copyAll = async () => {
    const values = [
      ['订单ID', currentOrder.id],
      ['店铺', currentOrder.storeName],
      ['三方订单号', currentOrder.orderNo],
      ['亚马逊订单号', currentOrder.platformOrderNo],
      ['买家号', currentOrder.customerId],
      [
        '订单状态',
        currentOrder.statusDesc || ORDER_STATUSES[currentOrder.status]?.text,
      ],
      ['物流状态', logisticsStatus.text],
      ['收货人', currentOrder.receivingName],
      ['电话', currentOrder.receivingPhone],
      ['地址', currentOrder.receivingAddress],
      ['城市', currentOrder.receivingCity],
      ['州/省', currentOrder.state],
      ['国家', currentOrder.receivingCountry],
      ['邮编', currentOrder.receivingPostCode],
      ['物流类型', currentOrder.logisticsType],
      ['承运商', currentOrder.carrier],
      ['跟踪号', currentOrder.trackingNumber],
      ['跟踪链接', currentOrder.trackingUrl],
      ['订单日期', currentOrder.orderDate],
      ['预计送达', currentOrder.deliveryDate],
      ['创建时间', currentOrder.createTime],
      ['更新时间', currentOrder.updateTime],
      ['订单金额', formatUsd(currentOrder.total)],
      ['失败原因', currentOrder.failContent],
      ['备注', currentOrder.remark],
    ];
    try {
      await navigator.clipboard.writeText(
        values
          .map(([label, value]) => label + ': ' + (value ?? '-'))
          .join('\n'),
      );
      message.success('订单信息已复制');
    } catch {
      message.error('复制失败，请手动选择信息复制');
    }
  };
  const fields = (entries) => (
    <dl className="detail-fields">
      {entries.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value ?? '-'}</dd>
        </div>
      ))}
    </dl>
  );
  const trackingUrl = /^https?:\/\//i.test(currentOrder.trackingUrl || '')
    ? currentOrder.trackingUrl
    : null;
  const logisticsFields = [
    ['物流类型', currentOrder.logisticsType],
    ['承运商', currentOrder.carrier],
    ['跟踪号', currentOrder.trackingNumber],
    ['跟踪链接', currentOrder.trackingUrl],
    ['预计送达', currentOrder.deliveryDate],
  ]
    .filter(([, value]) => value != null && value !== '')
    .map(([label, value]) => [
      label,
      label === '跟踪链接' && trackingUrl ? (
        <a href={trackingUrl} target="_blank" rel="noopener noreferrer">
          查看物流轨迹
        </a>
      ) : label === '跟踪号' ? (
        copyable(value)
      ) : (
        value
      ),
    ]);
  return (
    <Drawer
      title="订单详情"
      aria-label="订单详情"
      rootClassName="order-detail-drawer"
      open={visible}
      onClose={onClose}
      afterOpenChange={(open) => {
        if (!open) onAfterClose?.();
      }}
      width="min(720px, 100vw)"
      footer={
        navigation && (
          <div className="detail-navigation">
            <Button
              onClick={() => navigateOrder(navigation.onPrevious)}
              disabled={navigation.position <= 1}
            >
              上一单
            </Button>
            <span className="muted numeric">
              本页 {navigation.position} / {navigation.total}
            </span>
            <Button
              onClick={() => navigateOrder(navigation.onNext)}
              disabled={navigation.position >= navigation.total}
            >
              下一单
            </Button>
          </div>
        )
      }
      extra={
        <Button
          icon={<CopyOutlined />}
          onClick={copyAll}
          disabled={isLoading || !loadedOrder}
        >
          复制信息
        </Button>
      }
    >
      <div ref={contentRef} tabIndex={-1} className="detail-content">
        {currentError && (
          <Alert
            className="inline-error"
            type="error"
            showIcon
            message={currentError}
            description={
              hasSummary
                ? '下方仅保留订单摘要，完整详情尚未加载。'
                : '暂时无法查看关联订单，请重试或返回积分记录继续核对。'
            }
            action={
              <Button
                size="small"
                onClick={() => setRevision((value) => value + 1)}
              >
                重试
              </Button>
            }
          />
        )}
        {isLoading ? (
          <Skeleton active={false} paragraph={{ rows: 8 }} />
        ) : hasSummary ? (
          <>
            <div className="order-detail-summary">
              <div>
                <p className="detail-order-id">订单 ID · {currentOrder.id}</p>
                <h2>{currentOrder.orderNo || '未填写三方订单号'}</h2>
                <p className="detail-store">
                  {currentOrder.storeName || '未填写店铺'}
                </p>
                <Space wrap>
                  {status(
                    currentOrder.status,
                    ORDER_STATUSES,
                    currentOrder.statusDesc,
                  )}
                  <span className={'order-status ' + logisticsStatus.tone}>
                    {logisticsStatus.text}
                  </span>
                </Space>
              </div>
              <div className="detail-amount">
                <span>金额 USD</span>
                <strong className="numeric">
                  {formatUsd(currentOrder.total)}
                </strong>
              </div>
            </div>
            {currentOrder.failContent && (
              <Alert
                className="inline-error"
                type="error"
                showIcon
                message="处理失败"
                description={currentOrder.failContent}
              />
            )}
            {loadedOrder && (
              <>
                <section className="detail-section">
                  <h3 className="detail-section-title">商品明细</h3>
                  {currentOrder.products?.length ? (
                    <Table
                      size="small"
                      dataSource={currentOrder.products.map(
                        (product, index) => ({
                          ...product,
                          key: index,
                        }),
                      )}
                      pagination={false}
                      columns={[
                        {
                          title: 'ASIN',
                          dataIndex: 'asin',
                          render: (value) => (
                            <span className="order-number">{value}</span>
                          ),
                        },
                        {
                          title: '数量',
                          dataIndex: 'quantity',
                          align: 'right',
                          width: 60,
                        },
                        {
                          title: '预期价格 USD',
                          dataIndex: 'expectedPurchasePrice',
                          align: 'right',
                          width: 125,
                          render: formatUsd,
                        },
                      ]}
                    />
                  ) : (
                    <p className="detail-empty">暂无商品明细</p>
                  )}
                </section>
                <section className="detail-section">
                  <h3 className="detail-section-title">收货信息</h3>
                  {fields([
                    ['收货人', currentOrder.receivingName],
                    ['联系电话', copyable(currentOrder.receivingPhone)],
                  ])}
                  <dl className="detail-fields detail-fields-wide">
                    <div>
                      <dt>收货地址</dt>
                      <dd>
                        <span>{currentOrder.receivingAddress || '-'}</span>
                        <span className="detail-address-region">
                          {[
                            currentOrder.receivingCity,
                            currentOrder.state,
                            currentOrder.receivingPostCode,
                            currentOrder.receivingCountry,
                          ]
                            .filter(Boolean)
                            .join(' / ')}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </section>
                <section className="detail-section">
                  <h3 className="detail-section-title">订单信息</h3>
                  {fields([
                    ['买家号', currentOrder.customerId],
                    [
                      '亚马逊订单号',
                      currentOrder.platformOrderNo
                        ? copyable(currentOrder.platformOrderNo)
                        : '未关联',
                    ],
                    ['创建时间', currentOrder.createTime],
                    ['更新时间', currentOrder.updateTime],
                    ...(currentOrder.orderDate
                      ? [['订单日期', currentOrder.orderDate]]
                      : []),
                  ])}
                </section>
                <section className="detail-section">
                  <h3 className="detail-section-title">物流信息</h3>
                  {logisticsFields.length ? (
                    fields(logisticsFields)
                  ) : (
                    <p className="detail-empty">暂无物流明细</p>
                  )}
                </section>
                {currentOrder.priceInfo && (
                  <section className="detail-section">
                    <h3 className="detail-section-title">价格汇总</h3>
                    {fields([
                      ['订单总价', formatUsd(currentOrder.priceInfo.total)],
                      [
                        '支付总价',
                        formatUsd(currentOrder.priceInfo.paymentTotal),
                      ],
                      ['礼品卡优惠', formatUsd(currentOrder.priceInfo.gift)],
                      ['运费', formatUsd(currentOrder.priceInfo.shipping)],
                    ])}
                  </section>
                )}
                {currentOrder.remark && (
                  <section className="detail-section">
                    <h3 className="detail-section-title">备注</h3>
                    <p>{currentOrder.remark}</p>
                  </section>
                )}
              </>
            )}
          </>
        ) : null}
      </div>
    </Drawer>
  );
};
export default OrderDetail;
